import { makeAutoObservable, runInAction } from "mobx";
import { OsaCommentApi } from "../services/OsaCommentApi";
import {
  OsaComment,
  CreateOsaComment,
  UpdateOsaComment,
} from "../models/OsaComment";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaWayApi } from "../../osaway/services/OsaWayApi";

export class OsaCommentStore {
  rootStore;
  commentsByOsaWayId: Map<string, OsaComment[]> = new Map();
  showReviewNodeAttributes = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.commentsByOsaWayId = new Map();
      this.showReviewNodeAttributes = false;
    });
  }

  setShowReviewNodeAttributes(value: boolean) {
    runInAction(() => {
      this.showReviewNodeAttributes = value;
    });
  }

  async fetchComments(osaWayId: string, cancelIf?: () => boolean) {
    const { data: comments } = await OsaWayApi.listComments(
      osaWayId
    );
    if (cancelIf?.()) return;
    runInAction(() => {
      this.commentsByOsaWayId.set(osaWayId, comments);
      this.commentsByOsaWayId = new Map(
        this.commentsByOsaWayId
      );
    });
  }

  async postComment(request: CreateOsaComment) {
    const { data: comment } = await OsaCommentApi.create(request);

    runInAction(() => {
      // if comment is a reply, add it at the end of its main thread's replies
      this.updateOsaWayComments(request.osaWayId, (prev) => {
        if (comment.mainThreadId) {
          return prev.map((mainThread) => {
            if (mainThread.id === comment.mainThreadId) {
              return {
                ...mainThread,
                replies: [...mainThread.replies, comment],
              };
            }
            return mainThread;
          });
        } else {
          return [...prev, comment];
        }
      });
    });
  }

  private updateOsaWayComments(
    osaWayId: string,
    callback: (prev: OsaComment[]) => OsaComment[]
  ) {
    const comments =
      this.commentsByOsaWayId.get(osaWayId) ?? [];
    if (comments) {
      this.commentsByOsaWayId.set(osaWayId, callback(comments));
      this.commentsByOsaWayId = new Map(
        this.commentsByOsaWayId
      );
    }
  }

  async updateComment(commentId: string, request: UpdateOsaComment) {
    const { data: comment } = await OsaCommentApi.update(commentId, request);
    runInAction(() => {
      this.updateOsaWayComments(comment.osaWayId, (prev) => {
        return prev.map((mainThread) => {
          // if comment is a reply, go through the replies of its main thread
          if (comment.mainThreadId) {
            if (mainThread.id !== comment.mainThreadId) return mainThread;
            return {
              ...mainThread,
              replies: mainThread.replies.map((reply) =>
                reply.id === commentId ? comment : reply
              ),
            };
          }
          // else, replace the corresponding main thread
          return mainThread.id === commentId ? comment : mainThread;
        });
      });
    });
  }

  async resolveComment(commentId: string) {
    const response = await OsaCommentApi.resolve(commentId);
    runInAction(() => {
      this.updateOsaWayComments(
        response.data.osaWayId,
        (prev) => {
          return prev.map((comment) =>
            comment.id === commentId ? response.data : comment
          );
        }
      );
    });
  }

  async unresolveComment(commentId: string) {
    const response = await OsaCommentApi.unresolve(commentId);
    runInAction(() => {
      this.updateOsaWayComments(
        response.data.osaWayId,
        (prev) => {
          return prev.map((comment) =>
            comment.id === commentId ? response.data : comment
          );
        }
      );
    });
  }

  async deleteComment(comment: OsaComment) {
    await OsaCommentApi.delete(comment.id);
    runInAction(() => {
      this.updateOsaWayComments(comment.osaWayId, (prev) => {
        return (
          prev
            // if comment is a main thread, delete it like this
            .filter((mainThread) => mainThread.id !== comment.id)
            // if comment is a reply, delete it like this
            .map((mainThread) => ({
              ...mainThread,
              replies: mainThread.replies.filter(
                (reply) => reply.id !== comment.id
              ),
            }))
        );
      });
    });
  }
}
